import { InfiniteData, useInfiniteQuery, useQuery, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'
import { useAgentRuntime } from '../context/AgentProvider'
import {
  DataWithCursor,
  Activity,
  SortOrder,
  type ActivitySummary,
  type ActivitySummaryParams
} from '@multiplayer-app/ai-agent-types'

const STALE_TIME = 30 * 1000
const GC_TIME = 5 * 60 * 1000
const ACTIVITIES_PAGE_SIZE = 20

export const activityKeys = {
  all: ['mp-agents', 'activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...activityKeys.lists(), filters] as const,
  infinites: () => [...activityKeys.all, 'infinite'] as const,
  infinite: (filters: Record<string, unknown>) => [...activityKeys.infinites(), filters] as const,
  sourceTypes: (filters: Record<string, unknown>) => [...activityKeys.all, 'sourceTypes', filters] as const,
  detail: (id: string | undefined) => [...activityKeys.all, 'detail', id] as const,
  summary: (params?: ActivitySummaryParams) => [...activityKeys.all, 'summary', params] as const
} as const

export interface UseActivitiesParams {
  groupId?: string
  parentId?: string
  /** When set, only activities with this `sourceType` are returned */
  sourceType?: string
  sortField?: string
  sortOrder?: SortOrder
  pageSize?: number
  enabled?: boolean
  /** ISO date string or Date */
  from?: string | Date
  /** ISO date string or Date */
  to?: string | Date
  /** Tenant filter, e.g. { account: accountId } or { all: true } for super admin */
  tenant?: Record<string, string> | { all?: boolean }
  /** User ID filter; use '__all__' for all users in scope */
  ownerId?: string
}

export type UseActivitiesInfiniteListResult = UseInfiniteQueryResult<
  InfiniteData<DataWithCursor<Activity>, unknown>,
  Error
>

export interface UseActivitiesListParams extends UseActivitiesParams {
  /** 1-based page number */
  page: number
}

export type UseActivitiesListResult = UseQueryResult<DataWithCursor<Activity>, Error> & {
  page: number
  pageSize: number
  totalPages: number
  total: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

/** Flatten all pages from an infinite activities query into a single array. */
export const flattenInfiniteActivities = (
  data: InfiniteData<DataWithCursor<Activity>, unknown> | undefined
): Activity[] => data?.pages.flatMap((p) => p.data) ?? []

type TransportListParams = {
  groupId?: string
  parentId?: string
  sourceType?: string
  sort?: { field: string; order: SortOrder }
  skip: number
  limit: number
  from?: string
  to?: string
  tenant?: Record<string, string> | { all?: boolean }
  ownerId?: string
}

const buildListParams = (
  opts: {
    groupId?: string
    parentId?: string
    sourceType?: string
    sortField?: string
    sortOrder?: SortOrder
    from?: string | Date
    to?: string | Date
    tenant?: Record<string, string> | { all?: boolean }
    ownerId?: string
  },
  skip: number,
  limit: number
): TransportListParams => {
  const fromStr = opts.from instanceof Date ? opts.from.toISOString() : opts.from
  const toStr = opts.to instanceof Date ? opts.to.toISOString() : opts.to
  return {
    ...(opts.groupId && { groupId: opts.groupId }),
    ...(opts.parentId && { parentId: opts.parentId }),
    ...(opts.sourceType && { sourceType: opts.sourceType }),
    ...(opts.sortField && opts.sortOrder && { sort: { field: opts.sortField, order: opts.sortOrder } }),
    ...(fromStr && { from: fromStr }),
    ...(toStr && { to: toStr }),
    ...(opts.tenant && { tenant: opts.tenant }),
    ...(opts.ownerId && { ownerId: opts.ownerId }),
    skip,
    limit
  }
}

const emptyPage = (limit: number): DataWithCursor<Activity> => ({
  data: [],
  cursor: { total: 0, skip: 0, limit }
})

export const useActivitiesInfiniteList = (params?: UseActivitiesParams): UseActivitiesInfiniteListResult => {
  const runtime = useAgentRuntime()
  const {
    groupId,
    parentId,
    sourceType,
    enabled = true,
    sortOrder = SortOrder.Desc,
    sortField = 'createdAt',
    pageSize = ACTIVITIES_PAGE_SIZE,
    from,
    to,
    tenant,
    ownerId
  } = params ?? {}

  return useInfiniteQuery({
    queryKey: activityKeys.infinite({
      groupId,
      parentId,
      sourceType,
      sortField,
      sortOrder,
      pageSize,
      from,
      to,
      tenant,
      ownerId
    }),
    queryFn: async ({ pageParam }) => {
      if (!runtime.transport.listActivities) return emptyPage(pageSize)
      return runtime.transport.listActivities(
        buildListParams(
          { groupId, parentId, sourceType, sortField, sortOrder, from, to, tenant, ownerId },
          pageParam,
          pageSize
        )
      )
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.cursor.skip ?? 0) + lastPage.data.length
      return loaded < lastPage.cursor.total ? loaded : undefined
    },
    enabled: enabled && Boolean(runtime.transport.listActivities),
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })
}

export const useActivitiesList = (params: UseActivitiesListParams): UseActivitiesListResult => {
  const runtime = useAgentRuntime()
  const {
    groupId,
    parentId,
    sourceType,
    page,
    enabled = true,
    sortOrder = SortOrder.Desc,
    sortField = 'createdAt',
    pageSize = ACTIVITIES_PAGE_SIZE,
    from,
    to,
    tenant,
    ownerId
  } = params

  const skip = (page - 1) * pageSize

  const query = useQuery({
    queryKey: activityKeys.list({
      groupId,
      parentId,
      sourceType,
      sortField,
      sortOrder,
      pageSize,
      page,
      from,
      to,
      tenant,
      ownerId
    }),
    queryFn: async () => {
      if (!runtime.transport.listActivities) return emptyPage(pageSize)
      return runtime.transport.listActivities(
        buildListParams({ groupId, parentId, sourceType, sortField, sortOrder, from, to, tenant, ownerId }, skip, pageSize)
      )
    },
    enabled: enabled && Boolean(runtime.transport.listActivities) && page >= 1,
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })

  const total = query.data?.cursor.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    ...query,
    page,
    pageSize,
    totalPages,
    total,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1
  }
}

export const useActivity = (activityId: string | undefined) => {
  const runtime = useAgentRuntime()

  return useQuery({
    queryKey: activityKeys.detail(activityId),
    queryFn: async (): Promise<Activity | null> => {
      if (!activityId || !runtime.transport.getActivity) return null
      return runtime.transport.getActivity(activityId)
    },
    enabled: Boolean(activityId) && Boolean(runtime.transport.getActivity),
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })
}

export interface UseActivitySummaryParams extends ActivitySummaryParams {
  /** When false, query is disabled */
  enabled?: boolean
}

export type UseActivitySummaryResult = UseQueryResult<ActivitySummary[], Error>

export const useActivitySummary = (params?: UseActivitySummaryParams): UseActivitySummaryResult => {
  const runtime = useAgentRuntime()
  const { enabled = true, ...fetchParams } = params ?? {}

  return useQuery({
    queryKey: activityKeys.summary(fetchParams),
    queryFn: async (): Promise<ActivitySummary[]> => {
      if (!runtime.transport.getActivitySummary) return []
      return runtime.transport.getActivitySummary(fetchParams)
    },
    enabled: enabled && Boolean(runtime.transport.getActivitySummary),
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })
}

export interface UseActivitySourceTypesParams {
  groupId?: string
  parentId?: string
  /** ISO date string or Date */
  from?: string | Date
  /** ISO date string or Date */
  to?: string | Date
  tenant?: Record<string, string> | { all?: boolean }
  ownerId?: string
  enabled?: boolean
}

export type UseActivitySourceTypesResult = UseQueryResult<string[], Error>

export const useActivitySourceTypes = (params?: UseActivitySourceTypesParams): UseActivitySourceTypesResult => {
  const runtime = useAgentRuntime()
  const { enabled = true, groupId, parentId, from, to, tenant, ownerId } = params ?? {}
  const fromStr = from instanceof Date ? from.toISOString() : from
  const toStr = to instanceof Date ? to.toISOString() : to

  return useQuery({
    queryKey: activityKeys.sourceTypes({ groupId, parentId, from: fromStr, to: toStr, tenant, ownerId }),
    queryFn: async (): Promise<string[]> => {
      if (!runtime.transport.listActivitySourceTypes) return []
      return runtime.transport.listActivitySourceTypes({
        ...(groupId && { groupId }),
        ...(parentId && { parentId }),
        ...(fromStr && { from: fromStr }),
        ...(toStr && { to: toStr }),
        ...(tenant && { tenant }),
        ...(ownerId && { ownerId })
      })
    },
    enabled: enabled && Boolean(runtime.transport.listActivitySourceTypes),
    staleTime: STALE_TIME,
    gcTime: GC_TIME
  })
}
